/**
 * SurveyNotes Component
 * Displays and manages notes left by People Operations on survey sessions.
 * Allows admins to add, edit, and delete notes for discussion.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../services/api';
import '../styles/notes.css';

/**
 * Get auth headers for API calls
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
    };
};

/**
 * Get current user info from SWA principal
 */
const getCurrentUser = () => {
    const swaPrincipal = localStorage.getItem('swaPrincipal');
    if (swaPrincipal) {
        try {
            const decoded = JSON.parse(atob(swaPrincipal));
            return {
                email: decoded.userDetails || decoded.userId,
                name: decoded.userDetails || 'Admin'
            };
        } catch (e) {
            console.warn('Failed to decode SWA principal:', e);
        }
    }
    return { email: '', name: 'Admin' };
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
};

/**
 * Individual Note Component
 */
const NoteCard = ({ note, onEdit, onDelete, currentUserEmail }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(note.note_text);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const isAuthor = note.author_email === currentUserEmail;
    
    const handleSave = async () => {
        if (!editText.trim()) return;
        await onEdit(note.id, editText);
        setIsEditing(false);
    };
    
    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this note?')) {
            setIsDeleting(true);
            await onDelete(note.id);
        }
    };
    
    const handleCancel = () => {
        setEditText(note.note_text);
        setIsEditing(false);
    };
    
    // Get initials for avatar
    const getInitials = (name) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };
    
    // Get reference label
    const getReferenceLabel = () => {
        if (!note.reference_type || note.reference_type === 'general') return null;
        if (note.reference_type === 'question' && note.reference_id) {
            return `📌 Question ${note.reference_id}`;
        }
        if (note.reference_type === 'report' && note.reference_id) {
            return `📊 ${note.reference_id}`;
        }
        return null;
    };
    
    const referenceLabel = getReferenceLabel();
    
    return (
        <div className={`note-card ${isDeleting ? 'deleting' : ''}`}>
            <div className="note-header">
                <div className="note-author">
                    <div className="author-avatar" title={note.author_name}>
                        {getInitials(note.author_name)}
                    </div>
                    <div className="author-info">
                        <span className="author-name">{note.author_name}</span>
                        <span className="note-time">{formatDate(note.created_at)}</span>
                    </div>
                </div>
                {isAuthor && !isEditing && (
                    <div className="note-actions">
                        <button 
                            className="note-action-btn edit"
                            onClick={() => setIsEditing(true)}
                            title="Edit note"
                        >
                            ✏️
                        </button>
                        <button 
                            className="note-action-btn delete"
                            onClick={handleDelete}
                            title="Delete note"
                        >
                            🗑️
                        </button>
                    </div>
                )}
            </div>
            
            {referenceLabel && (
                <div className="note-reference">{referenceLabel}</div>
            )}
            
            {isEditing ? (
                <div className="note-edit-form">
                    <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        autoFocus
                    />
                    <div className="note-edit-actions">
                        <button className="btn-save" onClick={handleSave}>
                            Save
                        </button>
                        <button className="btn-cancel" onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <div className="note-content">
                    {note.note_text}
                </div>
            )}
            
            {note.updated_at !== note.created_at && !isEditing && (
                <div className="note-edited">(edited)</div>
            )}
        </div>
    );
};

/**
 * Main SurveyNotes Component
 */
function SurveyNotes({ sessionId, employeeName, referenceType, referenceId, compact = false }) {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExpanded, setIsExpanded] = useState(!compact);
    const [newNoteText, setNewNoteText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const currentUser = getCurrentUser();
    
    /**
     * Fetch notes for the session
     */
    const fetchNotes = useCallback(async () => {
        if (!sessionId) return;
        
        try {
            setIsLoading(true);
            const response = await fetch(
                `${API_BASE_URL}/api/admin/sessions/${sessionId}/notes`,
                { headers: getAuthHeaders() }
            );
            
            if (!response.ok) {
                throw new Error('Failed to load notes');
            }
            
            const data = await response.json();
            
            // Filter by reference if specified
            let filteredNotes = data.notes || [];
            if (referenceType && referenceType !== 'all') {
                filteredNotes = filteredNotes.filter(n => 
                    n.reference_type === referenceType &&
                    (!referenceId || n.reference_id === referenceId)
                );
            }
            
            setNotes(filteredNotes);
            setError(null);
        } catch (err) {
            console.error('Error fetching notes:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [sessionId, referenceType, referenceId]);
    
    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);
    
    /**
     * Add a new note
     */
    const handleAddNote = async () => {
        if (!newNoteText.trim() || isSubmitting) return;
        
        try {
            setIsSubmitting(true);
            const response = await fetch(
                `${API_BASE_URL}/api/admin/sessions/${sessionId}/notes`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        note_text: newNoteText.trim(),
                        reference_type: referenceType || 'general',
                        reference_id: referenceId || null
                    })
                }
            );
            
            if (!response.ok) {
                throw new Error('Failed to add note');
            }
            
            const newNote = await response.json();
            setNotes(prev => [newNote, ...prev]);
            setNewNoteText('');
        } catch (err) {
            console.error('Error adding note:', err);
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    /**
     * Edit an existing note
     */
    const handleEditNote = async (noteId, newText) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/admin/notes/${noteId}`,
                {
                    method: 'PUT',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({ note_text: newText })
                }
            );
            
            if (!response.ok) {
                throw new Error('Failed to update note');
            }
            
            const updatedNote = await response.json();
            setNotes(prev => prev.map(n => n.id === noteId ? updatedNote : n));
        } catch (err) {
            console.error('Error updating note:', err);
            setError(err.message);
        }
    };
    
    /**
     * Delete a note
     */
    const handleDeleteNote = async (noteId) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/admin/notes/${noteId}`,
                {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                }
            );
            
            if (!response.ok) {
                throw new Error('Failed to delete note');
            }
            
            setNotes(prev => prev.filter(n => n.id !== noteId));
        } catch (err) {
            console.error('Error deleting note:', err);
            setError(err.message);
        }
    };
    
    // Compact view - just show count and expand button
    if (compact && !isExpanded) {
        return (
            <div className="survey-notes-compact">
                <button 
                    className="notes-toggle-btn"
                    onClick={() => setIsExpanded(true)}
                >
                    💬 Notes ({notes.length})
                </button>
            </div>
        );
    }
    
    return (
        <div className="survey-notes">
            <div className="notes-header">
                <h3>
                    💬 Team Notes
                    {employeeName && <span className="notes-context"> - {employeeName}</span>}
                </h3>
                {compact && (
                    <button 
                        className="notes-collapse-btn"
                        onClick={() => setIsExpanded(false)}
                    >
                        ✕
                    </button>
                )}
            </div>
            
            {error && (
                <div className="notes-error">
                    {error}
                    <button onClick={fetchNotes}>Retry</button>
                </div>
            )}
            
            {/* Add new note form */}
            <div className="new-note-form">
                <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Add a note for your team... (e.g., 'Follow up on automation opportunities')"
                    rows={2}
                    disabled={isSubmitting}
                />
                <div className="new-note-actions">
                    <span className="note-author-hint">
                        Posting as {currentUser.name}
                    </span>
                    <button 
                        className="btn-add-note"
                        onClick={handleAddNote}
                        disabled={!newNoteText.trim() || isSubmitting}
                    >
                        {isSubmitting ? 'Adding...' : 'Add Note'}
                    </button>
                </div>
            </div>
            
            {/* Notes list */}
            <div className="notes-list">
                {isLoading ? (
                    <div className="notes-loading">Loading notes...</div>
                ) : notes.length === 0 ? (
                    <div className="notes-empty">
                        <span className="empty-icon">📝</span>
                        <p>No notes yet. Add the first note to start a discussion!</p>
                    </div>
                ) : (
                    notes.map(note => (
                        <NoteCard
                            key={note.id}
                            note={note}
                            onEdit={handleEditNote}
                            onDelete={handleDeleteNote}
                            currentUserEmail={currentUser.email}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

export default SurveyNotes;
